import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { SOCIETY_SLABS } from "@/data/site";

const inr = (n) => `₹${n.toLocaleString("en-IN")}`;

// Interactive: pick flats bracket + occupancy → exact per-screen payout from the slab.
export const OccupancyEstimator = () => {
  const { rows, cols, electricity } = SOCIETY_SLABS;
  const [flats, setFlats] = useState(2); // default >500 flats
  const [occ, setOcc] = useState(2); // default >90%
  const [screens, setScreens] = useState(6);

  const { rent, total, monthly } = useMemo(() => {
    const rent = rows[flats].values[occ];
    const total = rent + electricity;
    return { rent, total, monthly: total * screens };
  }, [flats, occ, screens, rows, electricity]);

  const Chip = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 rounded-md font-display font-600 text-sm transition-colors duration-200 border ${
        active ? "bg-teal text-ink border-teal" : "bg-transparent text-ink/60 border-ink/20 hover:border-ink/50"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="occupancy-estimator">
      <div className="lg:col-span-7 bg-white border border-ink/10 rounded-xl p-6 md:p-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-3">Number of flats</p>
          <div className="flex flex-wrap gap-2">
            {rows.map((r, i) => (
              <Chip key={r.label} active={flats === i} onClick={() => setFlats(i)} testid={`est-flats-${i}`}>{r.label}</Chip>
            ))}
          </div>
        </div>
        <div className="mt-7">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50 mb-3">Occupancy</p>
          <div className="flex flex-wrap gap-2">
            {cols.map((c, i) => (
              <Chip key={c} active={occ === i} onClick={() => setOcc(i)} testid={`est-occ-${i}`}>{c}</Chip>
            ))}
          </div>
        </div>
        <div className="mt-7">
          <label className="block">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-ink/50">Screens in your society</span>
            <input
              type="range" min="1" max="30" value={screens}
              onChange={(e) => setScreens(+e.target.value)}
              data-testid="est-screens" className="w-full mt-3 accent-teal"
            />
            <span className="font-display font-800 text-2xl">{screens}</span>
          </label>
        </div>
      </div>

      <div className="lg:col-span-5 bg-ink text-white rounded-xl p-6 md:p-8 flex flex-col">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-teal">Indicative payout</p>
        <motion.p
          key={total}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-3 font-display font-800 text-5xl tracking-tight"
          data-testid="est-total-per-screen"
        >
          {inr(total)}
        </motion.p>
        <p className="mt-2 text-sm text-white/50">per screen, per month</p>

        <div className="mt-8 space-y-px bg-white/10 rounded-lg overflow-hidden">
          {[
            ["Slab rent", inr(rent)],
            ["Electricity", `+ ${inr(electricity)}`],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between bg-ink px-4 py-3 text-sm">
              <span className="text-white/55">{k}</span>
              <span className="font-mono">{v}</span>
            </div>
          ))}
          <div className="flex justify-between bg-ink px-4 py-3 text-sm border-t border-white/10">
            <span className="text-white/80 font-600">Total / screen</span>
            <span className="font-mono text-teal">{inr(total)}</span>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-white/10 p-5">
          <p className="font-mono text-[10px] uppercase tracking-wider text-white/45">Your society ({screens} screens)</p>
          <p className="mt-1 font-display font-800 text-teal text-2xl" data-testid="est-monthly-total">{inr(monthly)} <span className="text-white/45 text-sm font-500">/ month</span></p>
        </div>
        <p className="mt-6 text-xs text-white/40 leading-relaxed">
          Indicative slab-based payout. Final commercial terms are confirmed per community; electricity is included.
        </p>
      </div>
    </div>
  );
};
